import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Banknote, Calculator, Eye, Printer, RotateCcw, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import * as api from '../api';
import { payrollSubUrl } from '../catalog';
import Modal from '../../hr/components/Modal';
import { useHR } from '../../hr/HRContext';
import {
  can, Chip, dmy, EditModal, ErrorBox, exportRows, invalidate, Kpi, Loading, matches, n2, naira, Panel, PeriodPick, plabel, PyHeader, SearchBox, Sel,
  Toolbar, usePyData, usePyMeta, usePyPeriod, Workbook,
} from '../py';
import { Approval } from '../shared';
import Loader from '../../hr/components/Loader';

// Stephanet's Payroll Runs & Approval (custom/pay15/Runs.jsx), Saved Payroll Register (the payroll_items register) and Saved Run Payslips
// (custom/Payslips.jsx). Kept from this app (the user's decisions): a run is approved on the approval engine (PAY_RUN) instead of a
// direct Approve button, so the run panel carries the approval route; Mark Paid is by hand after approval; an approved run is final.

const TONE = { Draft: 'gray', Calculated: 'blue', Submitted: 'amber', Returned: 'red', Approved: 'green', Paid: 'teal' };
const ACT = {
  submit: ['Submit for Approval', Send], reopen: ['Back to Draft', RotateCcw],
};
const TEXT = {
  submit: 'Sends the run to Payroll Review → HR Review → Finance Approval → Final Approval. While it is with the approvers it cannot be recalculated and the month\'s inputs are locked. Final approval books this month\'s loan repayments and savings withholdings, settles the HR expenses and leave paid through payroll and raises the PAYE, pension, NHF, NSITF and ITF remittances.',
  reopen: 'Returns the run to draft so it can be recalculated.',
};

export function PyRuns() {
  const { notify } = useHR();
  const [meta] = usePyMeta();
  const [period, setPeriod] = usePyPeriod(meta);
  const [d, , reloadData] = usePyData(period);
  const [x, setX] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [act, setAct] = useState(null);
  const [items, setItems] = useState(null);
  const load = useCallback(() => (period ? api.runs(period).then(setX).catch(e => setErr(e.message)) : null), [period]);
  useEffect(() => { load(); }, [load]);
  if (err) return <ErrorBox error={err} />;
  if (!x || !meta) return <Loading what="payroll runs" />;
  const cur = x.current;
  const save = async () => {
    setBusy(true);
    try { const r = await api.calculateRun(period); notify(r.message); invalidate(); load(); reloadData(); } catch (e) { notify(e.message, 'bad'); } finally { setBusy(false); }
  };
  const doAct = async (a, note) => {
    try { const r = await api.runAction(cur.id, { action: a, note }); notify(r.message); setAct(null); invalidate(); load(); } catch (e) { notify(e.message, 'bad'); }
  };
  const cols = [{ key: 'period', label: 'Period', w: 110, get: r => plabel(r.period) }, { key: 'status', label: 'Status', w: 100 }, { key: 'employees', label: 'Staff', kind: 'num', w: 70 },
    { key: 'gross', label: 'Gross', kind: 'money' }, { key: 'deductions', label: 'Deductions', kind: 'money' }, { key: 'net', label: 'Net pay', kind: 'money' },
    { key: 'employer', label: 'Employer cost on top', kind: 'money' }, { key: 'updated', label: 'Last change', w: 110, get: r => dmy(r.updated) }];
  const live = d?.totals;
  const canRun = x.can?.runs;
  return (
    <div className="ps py">
      <PyHeader title="Payroll Runs & Approval" sub="Save the month as a payroll run (calculated by the payroll engine), send it for approval — final approval books loan repayments, savings, HR-expense settlements and statutory remittances and creates the salary payment; the run is paid when that payment is recorded paid." meta={meta} period={period}>
        {canRun && <button className="mds-run" disabled={busy || !x.actions.includes('calculate')} onClick={save}><Calculator size={14} /> {busy ? 'Calculating…' : `${cur ? 'Recalculate' : 'Calculate & Save'} ${plabel(period)}`}</button>}
      </PyHeader>
      <div className="mdr-card ps-filters no-print"><PeriodPick meta={meta} value={period} onChange={setPeriod} />
        <div className="ps-runbar inline">Run for {plabel(period)}: {cur ? <Chip tone={TONE[cur.status]}>{cur.status.toLowerCase()}</Chip> : <Chip tone="amber">not saved</Chip>}</div></div>
      {live && <div className="ps-kpis five">
        <Kpi label="Live: Staff Paid" value={live.paid} />
        <Kpi label="Live: Gross" value={naira(live.gross)} tone="blue" i={1} note={cur ? `saved ${naira(cur.gross)}` : 'not saved yet'} />
        <Kpi label="Live: Deductions" value={naira(live.total_ded)} tone="red" i={2} />
        <Kpi label="Live: Net Pay" value={naira(live.net)} tone="green" i={3} note={cur && Math.abs(cur.net - live.net) > 0.5 ? `saved ${naira(cur.net)} — recalculate to update` : cur ? 'matches the saved run' : ''} />
        <Kpi label="Live: Cost to Company" value={naira(live.cost)} tone="navy" i={4} />
      </div>}
      {cur && <Panel title={`${plabel(cur.period)} run`} note={cur.code}>
        <div className="py-run-acts">
          {canRun && ['submit', 'reopen'].filter(a => x.actions.includes(a)).map(a => { const [l, I] = ACT[a]; return <button key={a} className={a === 'submit' ? 'mds-run' : 'mdr-btn'} onClick={() => setAct(a)}><I size={14} /> {l}</button>; })}
          {cur.status === 'Approved' && <Link className="mdr-btn" to={payrollSubUrl('payment-schedules')}><Banknote size={14} /> Salary payment PAY-{cur.code} — paid there</Link>}
          <button className="mdr-btn" onClick={() => api.runLines(cur.id).then(r => setItems(r)).catch(e => notify(e.message, 'bad'))}><Eye size={14} /> View Saved Lines</button>
        </div>
        {(x.route.length > 0 || x.at_me) && <div style={{ marginTop: 10 }}>
          <Approval route={x.route} history={x.history} actions={x.actions} onAction={doAct} skip={['submit', 'reopen', 'pay', 'calculate']} />
        </div>}
        {cur.notes && <pre className="py-notes-pre">{cur.notes}</pre>}
      </Panel>}
      <Workbook cols={cols} rows={x.runs} frozen={1} totals={false} onRow={r => setPeriod(r.period)} rowClass={r => (r.period === period ? 'on' : '')} empty="No payroll runs saved yet." />
      <Panel title="Approval Log" note="latest first">
        <ul className="py-log">{x.log.map((l, i) => <li key={i}><b>{l.action}</b><span>{l.summary}</span><small>{l.by} · {dmy(l.at)}</small></li>)}</ul>
      </Panel>
      {act && <ActModal a={act} run={cur} onClose={() => setAct(null)} onYes={note => doAct(act, note)} />}
      {items && <Modal title={`Saved lines — ${plabel(items.run.period)} (${items.run.status.toLowerCase()})`} wide onClose={() => setItems(null)}>
        <Workbook cols={[{ key: 'employee_no', label: 'Employee ID', w: 96 }, { key: 'name', label: 'Name', w: 170 }, { key: 'gross', label: 'Gross', kind: 'money' }, { key: 'paye', label: 'PAYE', kind: 'money' },
          { key: 'pension', label: 'Pension', kind: 'money' }, { key: 'loan', label: 'Loan', kind: 'money' }, { key: 'deductions', label: 'Deductions', kind: 'money' },
          { key: 'net', label: 'Net', kind: 'money' }, { key: 'employer', label: 'Employer statutory', kind: 'money' }]} rows={items.rows} frozen={2} maxHeight={480} />
      </Modal>}
    </div>
  );
}

function ActModal({ a, run, onClose, onYes }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [l] = ACT[a];
  return (
    <Modal title={`${l} — ${plabel(run.period)}`} onClose={onClose}>
      <p>{TEXT[a]}</p>
      <p className="muted">Net pay {n2(run.net)} for {run.employees} staff.</p>
      <label className="rec-f"><span>Note (optional)</span><textarea rows={2} value={note} onChange={e => setNote(e.target.value)} /></label>
      <div className="mdh-form-foot"><button className="mdr-btn" onClick={onClose}>Cancel</button>
        <button className="mds-run" disabled={busy} onClick={async () => { setBusy(true); try { await onYes(note); } finally { setBusy(false); } }}>{l}</button></div>
    </Modal>
  );
}

// ------------------------------------------------------------------ SAVED PAYROLL REGISTER (Stephanet's payroll_items register, read only)

const LINE_COLS = [
  { key: 'run', label: 'Payroll Run', w: 110 }, { key: 'employee_no', label: 'Employee ID', w: 96 }, { key: 'name', label: 'Employee', w: 170 },
  { key: 'days_worked', label: 'Days Worked', kind: 'num', w: 80 }, { key: 'basic', label: 'Basic', kind: 'money' }, { key: 'gross', label: 'Gross Pay', kind: 'money' },
  { key: 'paye', label: 'PAYE', kind: 'money' }, { key: 'total_deductions', label: 'Total Deductions', kind: 'money' }, { key: 'net', label: 'Net Pay', kind: 'money' },
];
const MORE_COLS = [
  ['transport', 'Transport'], ['other_allowances', 'Other Allowances'], ['overtime', 'Overtime'], ['bonus', 'Bonus'], ['pension_employee', 'Pension (EE)'], ['nhf', 'NHF'],
  ['loan_deduction', 'Loan Repayment'], ['missed_days_deduction', 'Missed Days'], ['other_deductions', 'Other Deductions'], ['pension_employer', 'Pension (ER)'],
  ['nsitf', 'NSITF'], ['itf', 'ITF'], ['reimbursements', 'Reimbursements (non-taxable)'], ['taxable_benefits', 'Taxable Benefits (non-cash)'],
].map(([key, label]) => ({ key, label, kind: 'money' }));

function useRuns() {
  const [runs, setRuns] = useState(null);
  useEffect(() => { api.runs().then(r => setRuns(r.runs)).catch(() => setRuns([])); }, []);
  return runs;
}

export function RunsRegister() {
  const runs = useRuns();
  const [run, setRun] = useState('');
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [all, setAll] = useState(false);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.runRegister(run || undefined).then(r => setRows(r.rows)).catch(e => setErr(e.message)), [run]);
  useEffect(() => { load(); }, [load]);
  const shown = useMemo(() => (rows || []).filter(r => matches(r, q, ['run', 'employee_no', 'name'])), [rows, q]);
  if (err) return <ErrorBox error={err} />;
  if (!rows || !runs) return <Loading what="the saved payroll register" />;
  const cols = all ? [...LINE_COLS, ...MORE_COLS] : LINE_COLS;
  return (
    <div className="ps py">
      <PyHeader title="Saved Payroll Register" sub="Every employee line of every saved payroll run — read only; the lines change only when a run is recalculated." />
      <div className="mdr-card ps-filters no-print">
        <Sel label="Payroll Run" value={run} onChange={setRun} options={runs.map(r => ({ value: String(r.id), label: `${r.code} · ${r.status}` }))} all="All runs" />
        <SearchBox value={q} onChange={setQ} placeholder="Search run, employee…" />
        <label className="rec-check"><input type="checkbox" checked={all} onChange={e => setAll(e.target.checked)} /> Show every column</label>
        <Toolbar onRefresh={load} onExport={() => exportRows('saved-payroll-register.csv', [...LINE_COLS, ...MORE_COLS], shown)} />
      </div>
      <Workbook cols={cols} rows={shown} frozen={3} onRow={setOpen} empty="No saved payroll lines yet — save a run in Payroll Runs & Approval." />
      {open && <Modal title={`${open.run} — ${open.employee_no} ${open.name}`} onClose={() => setOpen(null)}>
        <dl className="detail-grid">{[...LINE_COLS, ...MORE_COLS].map(c => <div key={c.key}><dt>{c.label}</dt><dd>{c.kind === 'money' ? n2(open[c.key]) : open[c.key] ?? '—'}</dd></div>)}</dl>
      </Modal>}
    </div>
  );
}

// ------------------------------------------------------------------ SAVED RUN PAYSLIPS (Stephanet's custom/Payslips.jsx)

function Payslip({ id, company, onBack }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => { api.payslip(id).then(setD).catch(e => setErr(e.message)); }, [id]);
  if (err) return <ErrorBox error={err} />;
  if (!d) return <Loader title="Loading payslip…" />;
  const { item: i, employee: e, run } = d;
  const x = i.detail || {};
  const earnings = [['Basic', i.basic], ['Transport', i.transport], ['Other Allowances', i.other_allowances], ['Overtime', i.overtime], ['Bonus', i.bonus]];
  const deductions = [['Pension (8%)', i.pension_employee], ['NHF', i.nhf], ['PAYE Tax', i.paye], ['Loan Repayment', i.loan_deduction], ['Missed Days', i.missed_days_deduction], ['Other Deductions', i.other_deductions]];
  return (
    <div className="panel payslip">
      <div className="toolbar no-print">
        <button className="btn ghost" onClick={onBack}>← All payslips</button>
        <div className="spacer" />
        <button className="btn" onClick={() => window.print()}><Printer size={15} /> Print</button>
      </div>
      <div className="payslip-head">
        <div className="payslip-brand">
          <img src="/rnl-logo.png" alt="" />
          <div><h2>{company || 'RIFITEL'}</h2></div>
        </div>
        <div className="right"><h3>PAYSLIP</h3><div>Period: <b>{run.period}</b></div></div>
      </div>
      <dl className="detail-grid">
        <div><dt>Employee</dt><dd>{e.full_name}</dd></div><div><dt>Employee ID</dt><dd>{e.employee_no}</dd></div>
        <div><dt>Department</dt><dd>{x.department || '—'}</dd></div><div><dt>Site</dt><dd>{x.site || '—'}</dd></div>
        <div><dt>Bank</dt><dd>{x.bank || '—'} {x.account || ''}</dd></div><div><dt>Days Worked</dt><dd>{n2(i.days_worked)}</dd></div>
      </dl>
      <div className="two-col">
        <table><thead><tr><th>Earnings</th><th className="num">Amount</th></tr></thead>
          <tbody>{earnings.map(([l, v]) => <tr key={l}><td>{l}</td><td className="num">{n2(v)}</td></tr>)}</tbody>
          <tfoot><tr><td>Gross Pay</td><td className="num">{n2(i.gross)}</td></tr></tfoot></table>
        <table><thead><tr><th>Deductions</th><th className="num">Amount</th></tr></thead>
          <tbody>{deductions.map(([l, v]) => <tr key={l}><td>{l}</td><td className="num">{n2(v)}</td></tr>)}</tbody>
          <tfoot><tr><td>Total Deductions</td><td className="num">{n2(i.total_deductions)}</td></tr></tfoot></table>
      </div>
      {(Number(i.reimbursements) > 0 || Number(i.taxable_benefits) > 0) && <div className="payslip-extra">
        {Number(i.reimbursements) > 0 && <div><span>Reimbursements (non-taxable, added to net pay)</span><b>{n2(i.reimbursements)}</b></div>}
        {Number(i.taxable_benefits) > 0 && <div><span>Taxable benefits in kind (taxed in PAYE, not paid in cash)</span><b>{n2(i.taxable_benefits)}</b></div>}
      </div>}
      <div className="net-pay">NET PAY <span>{n2(i.net)}</span></div>
      <div className="muted small">Employer contributions: Pension {n2(i.pension_employer)} · NSITF {n2(i.nsitf)} · ITF {n2(i.itf)}</div>
    </div>
  );
}

export function RunPayslips() {
  const [meta] = usePyMeta();
  const runs = useRuns();
  const [id, setId] = useState('');
  const [run, setRun] = useState('');
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!runs) return;
    if (!run) { const r = runs.find(x => ['Approved', 'Paid', 'Calculated'].includes(x.status)); if (r) setRun(String(r.id)); return; }
    api.runRegister(run).then(r => setRows(r.rows)).catch(() => setRows([]));
  }, [run, runs]);
  if (id) return <Payslip id={id} company={meta?.company} onBack={() => setId('')} />;
  if (!runs) return <Loading what="saved runs" />;
  const cols = [{ key: 'employee_no', label: 'Employee ID', w: 96 }, { key: 'name', label: 'Employee', w: 190 }, { key: 'days_worked', label: 'Days Worked', kind: 'num', w: 90 },
    { key: 'gross', label: 'Gross Pay', kind: 'money' }, { key: 'paye', label: 'PAYE', kind: 'money' }, { key: 'total_deductions', label: 'Total Deductions', kind: 'money' },
    { key: 'net', label: 'Net Pay', kind: 'money' }];
  return (
    <div className="panel">
      <div className="toolbar"><Sel label="Payroll Run" value={run} onChange={setRun} options={runs.map(r => ({ value: String(r.id), label: `${r.code} · ${r.status}` }))} all="—" /></div>
      <Workbook cols={cols} rows={run ? rows : []} frozen={2} onRow={r => setId(String(r.id))} empty="No saved payroll run yet." />
    </div>
  );
}

// ------------------------------------------------------------------ STATUTORY REMITTANCES (Stephanet's statutory_remittances register)

export function Remittances() {
  const { notify } = useHR();
  const [meta] = usePyMeta();
  const [x, setX] = useState(null);
  const [err, setErr] = useState('');
  const [st, setSt] = useState('');
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.remittances().then(setX).catch(e => setErr(e.message)), []);
  useEffect(() => { load(); }, [load]);
  if (err) return <ErrorBox error={err} />;
  if (!x) return <Loading what="statutory remittances" />;
  const rows = x.rows.filter(r => !st || r.status === st);
  const cols = [{ key: 'statutory_type', label: 'Type', w: 90 }, { key: 'period', label: 'Period (YYYY-MM)', w: 110 }, { key: 'run', label: 'Payroll Run', w: 110 },
    { key: 'authority', label: 'Authority / PFA', w: 200 }, { key: 'amount', label: 'Amount', kind: 'money' },
    { key: 'remitted_on', label: 'Remitted On', w: 110, get: r => (r.remitted_on ? dmy(r.remitted_on) : '') }, { key: 'receipt_no', label: 'Receipt No.', w: 120 },
    { key: 'status', label: 'Status', w: 90, get: r => r.status.toLowerCase() }];
  const editable = x.can?.runs && can(meta, 'statutory');
  return (
    <div className="ps py">
      <PyHeader title="Statutory Remittances" sub="PAYE, pension, NHF, NSITF and ITF raised when a payroll run is approved — due until remitted." />
      <div className="mdr-card ps-filters no-print">
        <Sel label="Status" value={st} onChange={setSt} options={['Due', 'Remitted']} />
        <Toolbar onRefresh={load} onExport={() => exportRows('statutory-remittances.csv', cols, rows)} />
      </div>
      <Workbook cols={cols} rows={rows} frozen={1} onRow={editable ? setEdit : undefined} empty="No remittances yet — they are raised when a payroll run is approved." />
      {edit && <EditModal title={`${edit.statutory_type} — ${plabel(edit.period)}`} value={{ ...edit, status: edit.status }} wide={false}
        fields={[{ k: 'status', label: 'Status', type: 'select', options: ['Due', 'Remitted'], blank: false }, { k: 'remitted_on', label: 'Remitted On', type: 'date' },
          { k: 'receipt_no', label: 'Receipt No.' }]}
        onSave={async f => { const r = await api.remit(edit.id, f); notify(r.message); setEdit(null); load(); }} onClose={() => setEdit(null)}>
        {() => edit.status === 'Due' && can(meta, 'bank-payments') && (
          <p className="muted small">Paying through Payslips &amp; Payments?{' '}
            <button type="button" className="mdr-btn" onClick={async () => { try { const r = await api.remittancePayment(edit.id); notify(r.message); setEdit(null); load(); } catch (e) { notify(e.message, 'bad'); } }}>
              <Banknote size={14} /> Create payment</button> — recording that payment paid marks this remittance remitted.</p>)}
      </EditModal>}
    </div>
  );
}
